'use client';

import { useEffect, useState } from 'react';
import { formatPrice, type Product } from '@/lib/products';
import { useLocale } from '@/context/LanguageContext';
import settingsData from '@/data/settings.json';
import { localeCurrency } from '@/i18n';
import {
  trackSubscriptionCheckoutButtonClicked,
  trackSubscriptionRedirectedToStripe,
} from '@/lib/analytics';
import { gaViewItem, gaBeginCheckout, getGaClientId, getGaSessionId } from '@/lib/ga';
import styles from './page.module.scss';

export default function SubscriptionPage() {
  const locale = useLocale();
  const currency = localeCurrency[locale];
  const standardShipping = settingsData.shipping.options[0];
  const subscriptionShipping = currency === 'eur'
    ? (standardShipping?.priceEUR ?? 0)
    : (standardShipping?.priceDKK ?? 0);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [selectedSize, setSelectedSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadProducts() {
      setLoadingProducts(true);
      setProductsError(null);
      try {
        const response = await fetch('/api/products', { cache: 'no-store' });
        const data = await response.json();
        if (!response.ok) {
          throw new Error('Kunne ikke hente produkter');
        }
        if (!Array.isArray(data)) {
          throw new Error('Ugyldigt produktsvar fra serveren');
        }
        const subscriptionProducts = (data as Product[]).filter(
          (p) => p.visibleInSubscription !== false
        );
        if (!mounted) return;
        setProducts(subscriptionProducts);
        setSelectedProduct(0);
        setSelectedSize(0);
      } catch (err) {
        if (!mounted) return;
        setProducts([]);
        setProductsError(err instanceof Error ? err.message : 'Kunne ikke hente produkter');
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    }

    loadProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const product = products[selectedProduct];
  const variant = product?.variants[selectedSize];

  useEffect(() => {
    if (product && variant) {
      gaViewItem(
        {
          slug: product.slug,
          size: variant.size,
          name: product.name,
          price: (currency === 'eur' ? variant.priceEur : variant.price) / 100,
        },
        currency
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.slug, variant?.size]);

  if (loadingProducts) {
    return (
      <section className={styles.subscription}>
        <div className="container">
          <div className={styles.content}>
            <h1 className={styles.title}>Kaffeabonnement</h1>
            <p className={styles.intro}>Indlæser produkter...</p>
          </div>
        </div>
      </section>
    );
  }

  if (productsError) {
    return (
      <section className={styles.subscription}>
        <div className="container">
          <div className={styles.content}>
            <h1 className={styles.title}>Kaffeabonnement</h1>
            <p className={styles.intro}>{productsError}</p>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className={styles.subscription}>
        <div className="container">
          <div className={styles.content}>
            <h1 className={styles.title}>Kaffeabonnement</h1>
            <p className={styles.intro}>
              Der er ingen produkter tilgængelige for abonnement lige nu.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const handleSubscribe = async () => {
    if (!product || !variant) return;

    const analyticsPrice = (currency === 'eur' ? variant.priceEur : variant.price) + subscriptionShipping;
    const analyticsProduct = { id: product.id, name: product.name };
    const analyticsVariant = { size: variant.size, price: analyticsPrice };
    trackSubscriptionCheckoutButtonClicked(analyticsProduct, analyticsVariant, currency);
    gaBeginCheckout(
      [
        {
          slug: product.slug,
          size: variant.size,
          name: product.name,
          price: (currency === 'eur' ? variant.priceEur : variant.price) / 100,
          quantity: 1,
        },
      ],
      currency
    );

    setLoading(true);
    setError(null);

    const [gaClientId, gaSessionId] = await Promise.all([
      getGaClientId(),
      getGaSessionId(),
    ]);

    try {
      const response = await fetch('/api/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stripePriceId: currency === 'eur'
            ? variant.stripeRecurringPriceIdEur
            : variant.stripeRecurringPriceId,
          productId: product.id,
          size: variant.size,
          locale,
          gaClientId,
          gaSessionId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Der opstod en fejl. Prøv igen.');
        setLoading(false);
        return;
      }

      if (data.url) {
        trackSubscriptionRedirectedToStripe(analyticsProduct, analyticsVariant, currency);
        window.location.assign(data.url);
      } else {
        setError('Kunne ikke starte abonnement. Prøv igen.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError('Netværksfejl – tjek din forbindelse og prøv igen.');
      setLoading(false);
    }
  };

  return (
    <section className={styles.subscription}>
      <div className="container">
        <div className={styles.content}>
          <h1 className={styles.title}>Kaffeabonnement</h1>
          <p className={styles.intro}>
            Få friskristet kaffe leveret til din dør hver måned. 
            Vælg din favoritkaffe og størrelse – vi klarer resten.
          </p>

          <div className={styles.configurator}>
            <div className={styles.step}>
              <h2>1. Vælg kaffe</h2>
              <div className={styles.options}>
                {products.map((p, i) => (
                  <button
                    key={p.id}
                    className={`${styles.option} ${i === selectedProduct ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedProduct(i);
                      setSelectedSize((prev) =>
                        prev >= products[i].variants.length
                          ? products[i].variants.length - 1
                          : prev
                      );
                    }}
                  >
                    <span className={styles.optionName}>{p.name}</span>
                    <span className={styles.optionFlavor}>{p.flavor}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.step}>
              <h2>2. Vælg størrelse</h2>
              <div className={styles.sizes}>
                {product.variants.map((v, i) => (
                  <button
                    key={v.size}
                    className={`${styles.sizeBtn} ${i === selectedSize ? styles.active : ''}`}
                    onClick={() => setSelectedSize(i)}
                  >
                    <span className={styles.sizeLabel}>{v.size}</span>
                    <span className={styles.sizePrice}>
                      {formatPrice((currency === 'eur' ? v.priceEur : v.price) + subscriptionShipping)}/md
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.summary}>
              <h3>Dit abonnement</h3>
              <p className={styles.summaryProduct}>{product.name}</p>
              <p className={styles.summarySize}>{variant.size}</p>
              <p className={styles.summaryPrice}>
                {formatPrice((currency === 'eur' ? variant.priceEur : variant.price) + subscriptionShipping)} / måned
              </p>
              <p className={styles.shippingIncluded}>Inkl. fragt</p>
              {error && (
                <div className={styles.error}>
                  <p>{error}</p>
                </div>
              )}
              <button
                className={styles.subscribe}
                onClick={handleSubscribe}
                disabled={loading}
              >
                {loading ? 'Vent venligst...' : 'Start abonnement'}
              </button>
              <p className={styles.note}>
                Du kan til enhver tid ændre eller annullere dit abonnement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
